import React from "react";
import { Box, Typography, Button, Container, FormGroup, FormControlLabel, Checkbox, useTheme, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import { getBrandLogo } from "../styles/branding";
import BrandingThemeToggle from "../components/BrandingThemeToggle";
import { useBrandAppearance } from "../hooks/useBrandAppearance";
import { getTenantConfig } from "../config/tenant";

const PrivacyPolicyPage: React.FC = () => {
    const { t } = useTranslation("privacy_policy");
    const theme = useTheme();
    const { colors: BrandColors, isDark: darkUi } = useBrandAppearance();
    const logo = getBrandLogo(darkUi ? "dark" : "light");
    const { navigateWithLang } = useNavigateWithLang();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const location = useLocation();
    const navigate = useNavigate();
    const fromGuestDetails = location.state?.fromGuestDetails === true;

    const tenant = getTenantConfig();
    const propertyName =
        (tenant?.chainName && String(tenant.chainName).trim()) ||
        t("fallbackPropertyName", { defaultValue: "this property" });

    /** Chain-specific HTML from chain-info; overrides locale templates when set. Trusted server content only. */
    const customPolicyHtml = tenant?.branding?.privacyPolicyHtml?.trim();

    const consentItemsRaw = t("consent_items", { returnObjects: true });
    const consentItems = Array.isArray(consentItemsRaw)
        ? (consentItemsRaw as string[])
        : [];

    const handleAgree = () => {
        if (fromGuestDetails) {
            navigate(-1);
        } else {
            navigateWithLang("/");
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                position: "relative",
                height: "100dvh",
                paddingBottom: 3,
                overflowY: "auto",
                backgroundColor: "var(--oc-page-bg)",
                color: "var(--oc-text)",
            }}
        >
            <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
                <BrandingThemeToggle variant={darkUi ? "onDark" : "onLight"} />
            </Box>

            <Container maxWidth="lg" sx={{ flexGrow: 1, paddingTop: 5 }}>
                <img
                    src={logo}
                    alt=""
                    style={{
                        height: isMobile ? "45px" : "70px",
                        width: "auto",
                    }}
                />
            </Container>

            <Container maxWidth="md" sx={{ flexGrow: 1, paddingTop: 2, paddingBottom: 4 }}>
                <Typography
                    component="div"
                    variant="body1"
                    sx={{
                        mb: 3,
                        "& p": { mb: 2 },
                        "& ul": { pl: 3, mb: 2 },
                    }}
                    dangerouslySetInnerHTML={{
                        __html: customPolicyHtml ?? t("policy_content", { propertyName }),
                    }}
                />
            </Container>

            {consentItems.length > 0 && (
                <Container maxWidth="md" sx={{ flexGrow: 0, paddingBottom: 10 }}>
                    <FormGroup sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {consentItems.map((label, index) => (
                            <FormControlLabel
                                key={index}
                                control={
                                    <Checkbox
                                        sx={{
                                            alignSelf: "flex-start",
                                            transform: "scale(1.4)",
                                            marginRight: 2,
                                            padding: 0.5,
                                            "&.Mui-checked": {
                                                color: BrandColors.primary,
                                            },
                                        }}
                                    />
                                }
                                label={label}
                                sx={{
                                    alignItems: "flex-start",
                                    m: 0,
                                    "& .MuiFormControlLabel-label": { color: "var(--oc-text)" },
                                }}
                            />
                        ))}
                    </FormGroup>
                </Container>
            )}

            <Box
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    backgroundColor: "var(--oc-footer-bg)",
                    padding: 0,
                    margin: 0,
                    zIndex: 100,
                }}
            >
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleAgree}
                    sx={{
                        backgroundColor: BrandColors.nextStep,
                        color: "#FFFFFF",
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        py: 2.5,
                        borderRadius: 0,
                        boxShadow: "0px -2px 5px rgba(0,0,0,0.1)",
                        "&:hover": { backgroundColor: BrandColors.nextStep },
                    }}
                >
                    {fromGuestDetails ? t("back_button") : t("agree_button")}
                </Button>
            </Box>
        </Box>
    );
};

export default PrivacyPolicyPage;
